import type { VorschauZeile } from '../lib/typen';
import { euro, prozent } from '../lib/format';

/**
 * Die ersten Zeilen der Datei, so wie der Import sie liest — mit den Preisen, die
 * daraus wuerden.
 *
 * Zweck (§11, Regel 2 „pro Bildschirm eine Aufgabe"): hier soll man an echten Zeilen
 * sehen, ob die richtige Datei erkannt wurde und ob die Preise plausibel sind, BEVOR
 * irgendetwas gespeichert oder geschrieben wird.
 *
 * Die Preisspalten kommen aus dem Preis-Set und stehen deshalb nicht fest.
 */
export default function VorschauTabelle({ zeilen }: { zeilen: VorschauZeile[] }) {
  if (zeilen.length === 0) {
    return (
      <p className="text-sm text-[#1A1A1A]/60">
        Keine Zeilen gelesen. Entweder ist die Datei leer, oder alle Zeilen sind durch
        die Filter des Profils gefallen.
      </p>
    );
  }

  // Preisarten in der Reihenfolge, in der das Set sie gerechnet hat.
  const preisarten = Object.keys(zeilen[0].preise);

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[900px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-[#DAD9D9] text-left text-xs uppercase tracking-wide text-[#1A1A1A]/60">
            <th className="py-2 pr-3 font-medium">Zeile</th>
            <th className="py-2 pr-3 font-medium">Nummer</th>
            <th className="py-2 pr-3 font-medium">Bezeichnung</th>
            <th className="py-2 pr-3 font-medium">Farbe</th>
            <th className="py-2 pr-3 font-medium">EAN</th>
            <th className="py-2 pr-3 text-right font-medium">Datei-EK</th>
            <th className="py-2 pr-3 text-right font-medium">Rabatt</th>
            {preisarten.map(art => (
              <th key={art} className="py-2 pr-3 text-right font-medium">{art}</th>
            ))}
            <th className="py-2 font-medium">Hinweise</th>
          </tr>
        </thead>
        <tbody>
          {zeilen.map(zeile => {
            const hinweise = [...zeile.hinweise, ...zeile.preisHinweise];
            return (
              <tr key={zeile.quellZeile} className="border-b border-[#DAD9D9]/50 align-top">
                <td className="py-2 pr-3 tabular-nums text-[#1A1A1A]/60">{zeile.quellZeile}</td>
                <td className="py-2 pr-3 tabular-nums">{zeile.nummer || '–'}</td>
                <td className="py-2 pr-3">{zeile.bezeichnung || '–'}</td>
                <td className="py-2 pr-3">{zeile.farbe || '–'}</td>
                <td className="py-2 pr-3 tabular-nums">
                  {zeile.ean ?? <span className="text-red-700">fehlt</span>}
                </td>
                <td className="py-2 pr-3 text-right tabular-nums">{euro(zeile.importEk)}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{prozent(zeile.rabatt)}</td>
                {preisarten.map(art => (
                  <td key={art} className="py-2 pr-3 text-right tabular-nums">
                    {zeile.preise[art] == null
                      ? <span className="text-[#1A1A1A]/40">–</span>
                      : euro(zeile.preise[art])}
                  </td>
                ))}
                <td className="py-2 text-xs text-amber-800">
                  {hinweise.length > 0 ? hinweise.join(' · ') : ''}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
